import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import React from 'react';

const optionFromChild = (child) => {
  if (!React.isValidElement(child)) return null;
  if (child.type !== 'option' && child.props?.value === undefined) return null;
  const label = child.props.children;
  return {
    value: child.props.value,
    label: typeof label === 'string' || typeof label === 'number' ? String(label) : String(child.props.value ?? ''),
    disabled: !!child.props.disabled,
  };
};

const CustomSelect = ({
  value,
  onChange,
  name,
  options: optionsProp,
  children,
  placeholder = 'Select...',
  searchable,
  size = 'md',
  isDisabled = false,
  isLoading = false,
  bg,
  color,
  borderColor,
  width,
  w,
  mb,
  mt,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const containerRef = React.useRef(null);

  const menuBg = useColorModeValue('white', 'navy.800');
  const defaultTriggerBg = useColorModeValue('#F6F7F9', 'gray.700');
  const defaultText = useColorModeValue('secondaryGray.900', 'white');
  const defaultBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const triggerBg = bg || defaultTriggerBg;
  const textColor = color || defaultText;
  const border = borderColor || defaultBorder;
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const selectedBg = useColorModeValue('#E8F6E3', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const childOptions = React.Children.toArray(children).map(optionFromChild).filter(Boolean);
  const options = (optionsProp && optionsProp.length ? optionsProp : childOptions).map((item) => ({
    value: item.value,
    label: item.label ?? item.title ?? item.name ?? String(item.value),
    disabled: item.disabled,
  }));

  const selected = options.find((item) => String(item.value) === String(value));
  const showSearch = searchable ?? options.length > 6;
  const filtered = options.filter((item) => {
    if (!query.trim()) return true;
    return String(item.label).toLowerCase().includes(query.trim().toLowerCase());
  });

  const emit = (next) => {
    if (!onChange) return;
    onChange({
      target: { name, value: next },
      currentTarget: { name, value: next },
    });
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box position="relative" ref={containerRef} w={w || width || '100%'} minW={size === 'sm' ? '80px' : undefined} mb={mb} mt={mt}>
      <Button
        type="button"
        onClick={() => !isDisabled && setIsOpen((open) => !open)}
        bg={triggerBg}
        color={selected ? textColor : muted}
        border="1px solid"
        borderColor={border}
        borderRadius="14px"
        width="100%"
        h={size === 'sm' ? '32px' : '42px'}
        px={size === 'sm' ? 3 : 4}
        fontSize={size === 'sm' ? 'sm' : 'md'}
        fontWeight="500"
        justifyContent="space-between"
        rightIcon={<ChevronDownIcon />}
        isDisabled={isDisabled}
        isLoading={isLoading}
        _hover={{ bg: triggerBg }}
        _active={{ bg: triggerBg }}
        _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
      >
        <Text as="span" noOfLines={1} textAlign="left" w="100%">
          {selected ? selected.label : placeholder}
        </Text>
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 6px)"
          left={0}
          right={0}
          bg={menuBg}
          border="1px solid"
          borderColor="rgba(17, 24, 18, 0.08)"
          borderRadius="14px"
          boxShadow="0 12px 32px rgba(17, 24, 18, 0.12)"
          zIndex={1400}
          overflow="hidden"
        >
          {showSearch && (
            <Box p={2} borderBottom="1px solid" borderColor={border}>
              <Input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                size="sm"
                autoFocus
              />
            </Box>
          )}
          <Box maxH="220px" overflowY="auto">
            {filtered.length === 0 ? (
              <Text p={3} fontSize="sm" color={muted} textAlign="center">
                No options found
              </Text>
            ) : (
              filtered.map((item) => {
                const isSelected = String(item.value) === String(value);
                return (
                  <Flex
                    key={String(item.value)}
                    px={3}
                    py={2}
                    cursor={item.disabled ? 'not-allowed' : 'pointer'}
                    opacity={item.disabled ? 0.5 : 1}
                    bg={isSelected ? selectedBg : 'transparent'}
                    _hover={item.disabled ? undefined : { bg: hoverBg }}
                    onClick={() => {
                      if (item.disabled) return;
                      emit(item.value);
                      setIsOpen(false);
                      setQuery('');
                    }}
                  >
                    <Text fontSize="sm" color={textColor} fontWeight={isSelected ? '700' : '500'}>
                      {item.label}
                    </Text>
                  </Flex>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomSelect;
